import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/download";
import { Search, ArrowLeft, Download, UserCheck, Pause, Play, Gift, CalendarPlus, Trash2, MoreVertical, ShieldAlert } from "lucide-react";
import BusinessTypeOverridePanel from "./BusinessTypeOverridePanel";


/**
 * DeleteCompanyDialog — password-reauth destructive confirmation.
 * Backend enforces: DELETE /api/admin/tenants/{id} now REQUIRES the
 * super-admin's own password in the request body. We double-check the
 * typed company name too as a client-side safety net.
 */
function DeleteCompanyDialog({ open, onOpenChange, org, onDeleted }) {
  const [password, setPassword] = useState("");
  const [nameCheck, setNameCheck] = useState("");
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setPassword(""); setNameCheck(""); } }, [open]);
  if (!org) return null;
  const nameMatches = nameCheck.trim().toLowerCase() === (org.name || "").trim().toLowerCase();
  const canSubmit = password.length >= 4 && nameMatches;
  const doDelete = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${org.id}`, { data: { password } });
      toast.success(`${org.name} — soft-deleted · 30-day hard-purge scheduled`);
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-neutral-950 border-red-500/30 text-foreground" data-testid="delete-tenant-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-red-600 mb-1">
            <ShieldAlert className="w-5 h-5" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Destructive action</span>
          </div>
          <DialogTitle className="text-xl">Delete <span className="text-red-600">{org.name}</span>?</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
            Soft-deletes every record scoped to this tenant. The company can be
            restored within <b className="text-foreground">30 days</b>; after that it
            is hard-purged and unrecoverable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-foreground/80 text-[11px] uppercase font-mono">Type the company name to confirm</Label>
            <Input value={nameCheck} onChange={e => setNameCheck(e.target.value)}
              placeholder={org.name}
              className="mt-1 bg-background border-border text-foreground" data-testid="delete-name-confirm" />
            {nameCheck && !nameMatches && (
              <div className="text-[10px] text-red-600 mt-1">Name doesn't match</div>
            )}
          </div>
          <div>
            <Label className="text-foreground/80 text-[11px] uppercase font-mono">Re-enter your super-admin password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1 bg-background border-border text-foreground" data-testid="delete-password" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
            className="rounded-sm border-border text-foreground/90 hover:bg-muted">Cancel</Button>
          <Button onClick={doDelete} disabled={!canSubmit || busy}
            className="rounded-sm bg-red-500 hover:bg-red-500/90 text-foreground gap-1.5" data-testid="delete-confirm-btn">
            <Trash2 className="w-4 h-4" />
            {busy ? "Deleting…" : "Delete company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function UsageBar({ label, used, cap, display, sub }) {
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const tone = pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-muted-foreground">{label}</div>
      <div className="text-lg font-bold mt-0.5">{display ?? `${used} / ${cap}`}</div>
      <div className="w-full h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {sub && <div className="text-[10px] text-muted-foreground/80 mt-1">{sub}</div>}
    </div>
  );
}

export function AdminTenantsList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const load = useCallback(async () => {
    const params = { ...(q ? { q } : {}), ...(state ? { state } : {}) };
    setRows((await api.get("/admin/tenants", { params })).data);
  }, [q, state]);
  useEffect(() => { load(); }, [load]);

  const rowImpersonate = async (o) => {
    try {
      await api.post(`/admin/tenants/${o.id}/impersonate`);
      toast.success("Impersonation started · opening tenant app");
      window.open("/app", "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rowExport = (o) => downloadBlob(`/admin/tenants/${o.id}/export.json`, `tenant_${o.id.slice(0,8)}.zip`);
  const rowSuspendToggle = async (o) => {
    const path = o.is_suspended ? `/admin/tenants/${o.id}/reactivate` : `/admin/tenants/${o.id}/suspend`;
    try { await api.post(path); toast.success(o.is_suspended ? "Reactivated" : "Suspended"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 space-y-4 text-foreground" data-testid="admin-tenants-list">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// TENANTS</div>
          <h1 className="text-3xl font-bold mt-1">All companies</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground/80" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / GSTIN"
              className="pl-8 bg-card text-foreground border-border rounded-sm w-64" data-testid="tenant-search" />
          </div>
          <Select value={state || "all"} onValueChange={(v) => setState(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 rounded-sm bg-card text-foreground border-border" data-testid="tenant-state-filter"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="complimentary">Complimentary</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="rounded-sm bg-card border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-card">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">State</th>
              <th className="text-left p-3">Trial ends</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3 pr-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((o) => (
              <tr key={o.id} data-testid={`tenant-row-${o.id.slice(0,8)}`} className="hover:bg-muted/60">
                <td className="p-3">
                  <div className="font-medium">{o.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground/80">{o.gstin || o.id.slice(0, 8)}</div>
                </td>
                <td className="p-3 font-mono uppercase text-[11px]">{o.plan || "business"}</td>
                <td className="p-3">
                  {o.is_deleted ? <span className="text-red-600 text-[11px] font-mono uppercase">DELETED</span>
                    : o.is_suspended ? <span className="text-amber-600 text-[11px] font-mono uppercase">SUSPENDED</span>
                    : o.is_complimentary ? <span className="text-purple-600 text-[11px] font-mono uppercase">COMP</span>
                    : <span className="text-emerald-600 text-[11px] font-mono uppercase">ACTIVE</span>}
                </td>
                <td className="p-3 text-muted-foreground">{o.trial_ends_at ? new Date(o.trial_ends_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-muted-foreground text-[11px] font-mono">{o.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-right pr-4">
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/admin/tenants/${o.id}`} className="text-orange-600 hover:underline text-[11px]" data-testid={`tenant-open-${o.id.slice(0,8)}`}>Open →</Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted" data-testid={`tenant-menu-${o.id.slice(0,8)}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-neutral-950 border-border text-foreground">
                        <DropdownMenuItem onClick={() => rowImpersonate(o)} className="gap-2 focus:bg-muted" data-testid={`tenant-impersonate-${o.id.slice(0,8)}`}>
                          <UserCheck className="w-4 h-4" /> Impersonate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => rowExport(o)} className="gap-2 focus:bg-muted" data-testid={`tenant-export-${o.id.slice(0,8)}`}>
                          <Download className="w-4 h-4" /> Export snapshot (ZIP)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => rowSuspendToggle(o)} className="gap-2 focus:bg-muted" data-testid={`tenant-suspend-${o.id.slice(0,8)}`}>
                          {o.is_suspended
                            ? <><Play className="w-4 h-4 text-emerald-600" /> Reactivate</>
                            : <><Pause className="w-4 h-4 text-amber-600" /> Suspend</>}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-muted" />
                        <DropdownMenuItem onClick={() => setDeleteTarget(o)}
                          disabled={o.is_deleted}
                          className="gap-2 text-red-600 focus:bg-red-500/10 focus:text-red-300" data-testid={`tenant-delete-${o.id.slice(0,8)}`}>
                          <Trash2 className="w-4 h-4" /> Delete company
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground/80 italic">No tenants match</td></tr>}
          </tbody>
        </table>
      </Card>
      <DeleteCompanyDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        org={deleteTarget} onDeleted={load} />
    </div>
  );
}

export function AdminTenantDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const load = useCallback(async () => setD((await api.get(`/admin/tenants/${id}`)).data), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const org = d.org, usage = d.usage;

  const call = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(path, body || {}); toast.success(msg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const impersonate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${id}/impersonate`);
      toast.success("Impersonation started · opening tenant app");
      window.open("/app", "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-6 text-foreground" data-testid="admin-tenant-detail">
      <div className="flex items-center gap-3">
        <Link to="/admin/tenants" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /></Link>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// TENANT</div>
          <h1 className="text-3xl font-bold mt-0.5">{org.name}</h1>
          <div className="text-[11px] font-mono text-muted-foreground/80 mt-0.5">{org.id} · {org.gstin || "no gstin"}</div>
        </div>
      </div>

      {/* Usage vs Plan Caps */}
      {d.billing && (
        <Card className="p-4 rounded-sm bg-card border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Plan & usage</div>
            <span className="text-[10px] font-mono uppercase text-muted-foreground">Plan: <span className="text-foreground">{d.billing.plan}</span></span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <UsageBar label="Seats" used={d.billing.seats.used} cap={d.billing.seats.bundled}
              sub={d.billing.seats.extras ? `+${d.billing.seats.extras} extra = ₹${d.billing.seats.extra_cost_inr}/mo` : "Within bundle"} />
            <UsageBar label="Storage" used={d.billing.storage.pct} cap={100}
              display={`${(d.billing.storage.used_bytes / (1024**3)).toFixed(2)} / ${d.billing.storage.cap_gb} GB`} />
            <UsageBar label="AI queries" used={d.billing.ai_queries.used} cap={d.billing.ai_queries.cap}
              display={`${d.billing.ai_queries.used} / ${d.billing.ai_queries.cap}`} />
            <UsageBar label="Emails" used={d.billing.email.used} cap={d.billing.email.cap}
              display={`${d.billing.email.used} / ${d.billing.email.cap === 999999 ? "∞" : d.billing.email.cap}`} />
          </div>
        </Card>
      )}

      {/* Usage */}
      <Card className="p-4 rounded-sm bg-card border-border">
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600 mb-3">Usage · live counts</div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[["Invoices", usage.invoices], ["Bills", usage.bills], ["Vouchers", usage.vouchers],
             ["Accounts", usage.accounts], ["Items", usage.items], ["Users", usage.users]].map(([l, v]) => (
            <div key={l} className="text-center">
              <div className="text-2xl font-bold">{v}</div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground mt-0.5">{l}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-border text-[11px] text-muted-foreground font-mono">
          Last activity: {usage.last_activity_at?.slice(0, 19).replace("T", " ") || "—"}
        </div>
      </Card>

      {/* Billing / Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 rounded-sm bg-card border-border space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Billing controls</div>
          <div className="text-[11px] font-mono text-muted-foreground">
            Plan: <span className="text-foreground">{org.plan || "business"}</span> ·
            Trial ends: <span className="text-foreground">{org.trial_ends_at?.slice(0, 10) || "—"}</span> ·
            Complimentary: <span className="text-foreground">{org.is_complimentary ? "yes" : "no"}</span>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label className="text-foreground/80">Extend trial (days)</Label>
              <Input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value || "0", 10))} className="mt-1 bg-background text-foreground border-border" />
            </div>
            <Button onClick={() => call(`/admin/tenants/${id}/extend-trial`, { days }, `Trial extended by ${days} days`)}
              disabled={busy} className="rounded-sm bg-orange-500 hover:bg-orange-500/90 text-foreground gap-1.5" data-testid="tenant-extend-trial">
              <CalendarPlus className="w-4 h-4" /> Extend
            </Button>
          </div>
          <div>
            <Label className="text-foreground/80">Grant free / complimentary access</Label>
            <div className="flex gap-2 mt-1">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="bg-background text-foreground border-border" />
              <Button onClick={() => call(`/admin/tenants/${id}/grant-complimentary`, { reason }, "Marked as complimentary")}
                disabled={busy} className="rounded-sm bg-purple-500 hover:bg-purple-500/90 text-foreground gap-1.5" data-testid="tenant-grant-free">
                <Gift className="w-4 h-4" /> Grant
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-sm bg-card border-border space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600 mb-1">Ops actions</div>
          <Button onClick={impersonate} disabled={busy} variant="outline"
            className="w-full rounded-sm border-border text-foreground hover:bg-muted justify-start gap-2" data-testid="tenant-impersonate">
            <UserCheck className="w-4 h-4" /> Impersonate tenant (audit-logged)
          </Button>
          {org.is_suspended ? (
            <Button onClick={() => call(`/admin/tenants/${id}/reactivate`, null, "Reactivated")} disabled={busy}
              variant="outline" className="w-full rounded-sm border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 justify-start gap-2" data-testid="tenant-reactivate">
              <Play className="w-4 h-4" /> Reactivate
            </Button>
          ) : (
            <Button onClick={() => call(`/admin/tenants/${id}/suspend`, null, "Suspended")} disabled={busy}
              variant="outline" className="w-full rounded-sm border-amber-500/30 text-amber-600 hover:bg-amber-500/10 justify-start gap-2" data-testid="tenant-suspend">
              <Pause className="w-4 h-4" /> Suspend
            </Button>
          )}
          <Button onClick={() => downloadBlob(`/admin/tenants/${id}/export.json`, `tenant_${id.slice(0,8)}.zip`)}
            variant="outline" className="w-full rounded-sm border-border text-foreground hover:bg-muted justify-start gap-2" data-testid="tenant-export">
            <Download className="w-4 h-4" /> Export snapshot (ZIP)
          </Button>
          <Button onClick={() => setDeleteOpen(true)} disabled={busy || org.is_deleted} variant="outline"
            className="w-full rounded-sm border-red-500/30 text-red-600 hover:bg-red-500/10 justify-start gap-2" data-testid="tenant-delete">
            <Trash2 className="w-4 h-4" /> Delete company (password required)
          </Button>
        </Card>
      </div>

      <DeleteCompanyDialog open={deleteOpen} onOpenChange={setDeleteOpen} org={org} onDeleted={load} />

      {/* Iter-189 · Business-type + per-feature override panel */}
      <BusinessTypeOverridePanel orgId={id} initial={org} onSaved={load} />

      {/* Users */}
      <Card className="p-4 rounded-sm bg-card border-border">
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600 mb-2">Users ({d.users.length})</div>
        <div className="divide-y divide-white/10">
          {d.users.map((u) => (
            <div key={u.id} className="py-2 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{u.full_name}</div>
                <div className="text-[11px] text-muted-foreground/80 font-mono">{u.email}</div>
              </div>
              <div className="text-[11px] font-mono uppercase text-muted-foreground">{u.role}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
